/**
 * Statement review upload card — a thin wrapper over the schema-driven
 * <LeadForm> (src/forms/definitions/statementReview.jsx). Files are staged in
 * S3 by the engine and attached to the Zoho Lead by the backend.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, FileText, Shield } from "lucide-react";
import LeadForm from "@/forms/engine/LeadForm";
import statementReview from "@/forms/definitions/statementReview";

function ConfirmationState() {
  return (
    <div className="text-center py-8" data-testid="sr-success">
      <div className="w-16 h-16 rounded-2xl bg-brand-aqua flex items-center justify-center mx-auto mb-5">
        <CheckCircle size={28} className="text-white" />
      </div>
      <h3 className="font-heading text-2xl font-bold text-brand-black mb-3">{statementReview.success.title}</h3>
      <p className="text-sm text-brand-grey leading-relaxed max-w-md mx-auto mb-4">{statementReview.success.body}</p>
      <p className="text-xs text-brand-grey mb-6">If you would like to speak to someone sooner, you can also book a quick call.</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a href="/book" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center justify-center gap-2 transition-colors btn-shine" data-testid="sr-book-call">Book a quick call <ArrowRight size={16} /></a>
        <Link to="/" className="bg-white border border-brand-aqua text-brand-aqua-dark font-semibold rounded-full px-8 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-brand-aqua-light transition-colors" data-testid="sr-back-home">Back to website</Link>
      </div>
    </div>
  );
}

function FormHeader() {
  return (
    <div className="bg-brand-aqua-light px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-brand-aqua/10">
      <div className="w-10 h-10 rounded-xl bg-brand-aqua flex items-center justify-center"><FileText size={20} className="text-white" /></div>
      <div>
        <h3 className="font-heading text-lg font-bold text-brand-black">Upload your statement for a tailored review</h3>
        <p className="text-xs text-brand-grey">Upload a recent card processing statement and we&apos;ll review it for you</p>
      </div>
    </div>
  );
}

const trustBadges = (
  <div className="flex flex-wrap items-center gap-4 text-xs text-brand-grey">
    <span className="flex items-center gap-1"><Shield size={12} className="text-brand-aqua" /> Secure upload</span>
    <span className="flex items-center gap-1"><CheckCircle size={12} className="text-brand-aqua" /> No obligation</span>
    <span className="flex items-center gap-1"><CheckCircle size={12} className="text-brand-aqua" /> Used only for review</span>
  </div>
);

export default function StatementReviewUpload({ className = "" }) {
  const [done, setDone] = useState(false);
  return (
    <div className={`bg-white border border-brand-light-grey rounded-2xl shadow-sm overflow-hidden ${className}`} data-testid="statement-review-upload">
      {!done && <FormHeader />}
      <div className="p-6 sm:p-8">
        <LeadForm
          schema={statementReview}
          header={trustBadges}
          onSuccess={() => setDone(true)}
          renderSuccess={() => <ConfirmationState />}
        />
      </div>
    </div>
  );
}
